// src/utils/ocr/labResultsNormalizer.ts
import {
  LAB_CBC_OBSERVATION_CODE_MAP,
  LAB_LIPID_OBSERVATION_CODE_MAP,
  LAB_REPORT_NAME_MAP,
  LAB_TYPES,
} from '@constants/option-types/lab';

// BBoxOccurrenceMap: each key maps to an ordered array of occurrences (one per repeated label).
// Shape matches what DynamicResourceForms.buildFlatAndMap puts in record._bboxMap.
export type BBoxOccurrenceEntry = { text?: string | null };
export type BBoxOccurrenceMap = Record<string, BBoxOccurrenceEntry[]>;

type LabResultRow = {
  id: string;
  category: string;
  code: string;
  displayName: string;
  testDate: string;
  unit: string;
  value: string;
};

export type LabResultsNormalized = {
  title: string;
  practitioner: string;
  facilityName: string;
  city: string;
  country: string;
  code: string;
  vitallCode: string;
  testDate: string;
  typeName: string;
  results: LabResultRow[];
};

const VALID_PANEL_CODES = new Set<string>(LAB_TYPES);

/** Collapse whitespace and strip punctuation for fuzzy panel-name lookup. */
function normalizeTypeNameKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.,;:]+/g, '')
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function registerPanelAlias(
  map: Record<string, string>,
  alias: string,
  vitallCode: string
): void {
  map[alias.trim().toLowerCase()] = vitallCode;
  map[normalizeTypeNameKey(alias)] = vitallCode;
}

// Panel display name (and common aliases) → vitallCode (labs--*)
// Built from LAB_REPORT_NAME_MAP so it stays in sync with the option list.
const PANEL_NAME_TO_CODE: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [vitallCode, name] of Object.entries(LAB_REPORT_NAME_MAP)) {
    registerPanelAlias(m, name, vitallCode);
    registerPanelAlias(m, vitallCode, vitallCode);
  }
  // Common short aliases not present in the display map
  const aliases: Array<[string, string]> = [
    ['cbc', 'labs--cbc'],
    ['complete blood count', 'labs--cbc'],
    ['complete blood count (cbc)', 'labs--cbc'],
    ['lipid panel', 'labs--lipids'],
    ['lipids panel', 'labs--lipids'],
    ['cmp', 'labs--chemistry'],
    ['comprehensive metabolic panel', 'labs--chemistry'],
    ['bmp', 'labs--electrolytes'],
    ['basic metabolic panel', 'labs--electrolytes'],
    ['lft', 'labs--liver-function'],
    ['liver function test', 'labs--liver-function'],
    ['liver function tests', 'labs--liver-function'],
    ['thyroid panel', 'labs--thyroid'],
    ['thyroid function', 'labs--thyroid'],
    ['tsh', 'labs--thyroid'],
    ['urinalysis', 'labs--urine-analyses'],
    ['ua', 'labs--urine-analyses'],
    ['abg', 'labs--arterial-blood-gases'],
    ['arterial blood gas', 'labs--arterial-blood-gases'],
  ];
  for (const [alias, code] of aliases) {
    registerPanelAlias(m, alias, code);
  }
  return m;
})();

const FALLBACK_PANEL_CODE = 'labs--other';

// CBC: display name → internal observation code (case-insensitive).
// Inverts LAB_CBC_OBSERVATION_CODE_MAP so "Hemoglobin" → "hemoglobin".
// Also maps code → code so raw OCR codes like "wbc" resolve correctly.
const CBC_DISPLAY_TO_CODE: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [code, display] of Object.entries(LAB_CBC_OBSERVATION_CODE_MAP)) {
    m[display.toLowerCase()] = code;
    if (!m[code.toLowerCase()]) m[code.toLowerCase()] = code;
  }
  return m;
})();

// Lipids: display name → internal observation code (case-insensitive).
// Inverts LAB_LIPID_OBSERVATION_CODE_MAP; adds common OCR aliases for cholesterol.
const LIPID_DISPLAY_TO_CODE: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [code, display] of Object.entries(
    LAB_LIPID_OBSERVATION_CODE_MAP
  )) {
    m[display.toLowerCase()] = code;
    if (!m[code.toLowerCase()]) m[code.toLowerCase()] = code;
  }
  m.cholesterol = 'totalcholesterol';
  m['total cholesterol'] = 'totalcholesterol';
  return m;
})();

function getText(
  occurrences: BBoxOccurrenceEntry[] | undefined,
  i: number
): string {
  return occurrences?.[i]?.text?.trim() ?? '';
}

/**
 * Resolve OCR/model typeName to a valid vitall panel code.
 * Accepts internal codes (labs--*) and display names; unknown → labs--other.
 */
function resolvePanelCode(typeName: string): string {
  const trimmed = typeName.trim();
  if (!trimmed) {
    return FALLBACK_PANEL_CODE;
  }

  const lower = trimmed.toLowerCase();
  if (VALID_PANEL_CODES.has(lower)) {
    return lower;
  }

  const normalizedKey = normalizeTypeNameKey(trimmed);
  if (VALID_PANEL_CODES.has(normalizedKey)) {
    return normalizedKey;
  }

  const fromLower = PANEL_NAME_TO_CODE[lower];
  if (fromLower) {
    return fromLower;
  }

  const fromNormalized = PANEL_NAME_TO_CODE[normalizedKey];
  if (fromNormalized) {
    return fromNormalized;
  }

  return FALLBACK_PANEL_CODE;
}

// For known panels, attempt to map rawCode / displayName to the internal code.
// Unknown observations fall back to rawCode (may be empty), which is fine —
// the form still renders displayName + value + unit.
function resolveObservationCode(
  rawCode: string,
  displayName: string,
  panelCode: string
): string {
  if (panelCode === 'labs--cbc') {
    return (
      CBC_DISPLAY_TO_CODE[rawCode.trim().toLowerCase()] ||
      CBC_DISPLAY_TO_CODE[displayName.trim().toLowerCase()] ||
      rawCode
    );
  }
  if (panelCode === 'labs--lipids') {
    return (
      LIPID_DISPLAY_TO_CODE[rawCode.trim().toLowerCase()] ||
      LIPID_DISPLAY_TO_CODE[displayName.trim().toLowerCase()] ||
      rawCode
    );
  }
  return rawCode;
}

/**
 * Normalizes a LabResults _bboxMap (which may have repeated row-level labels)
 * into a parent record + results[] array.
 *
 * Shared labels (typeName, title, testDate, performer) are read once from the
 * first occurrence.  Row labels (code, displayName/display/disply, value, unit)
 * are grouped by occurrence index.  Rows where code + displayName + value are all
 * empty are skipped (unit-only rows).
 *
 * When no row-level labels are present the function falls back to `flat` to
 * produce a single result row (preserves behaviour for plain single-value OCR).
 */
export function labResultsNormalizer(
  bboxMap: BBoxOccurrenceMap,
  flat: Record<string, any> = {}
): LabResultsNormalized {
  // --- shared parent fields ---
  const typeName = getText(bboxMap['typeName'], 0) || flat.typeName || '';
  const title = getText(bboxMap['title'], 0) || flat.title || '';
  const testDate = getText(bboxMap['testDate'], 0) || flat.testDate || '';
  const practitioner =
    getText(bboxMap['performer'], 0) ||
    flat.performer ||
    flat.practitioner ||
    '';
  // The facility block usually arrives from the group's Location resource, which
  // mergeOcrFromConfig has already resolved into `flat` by the time it calls this
  // assembler. Re-read it here because the assembler's return value *replaces*
  // the merged object rather than extending it.
  const facilityName =
    getText(bboxMap['facilityName'], 0) || flat.facilityName || '';
  const city = getText(bboxMap['city'], 0) || flat.city || '';
  const country = getText(bboxMap['country'], 0) || flat.country || '';

  const panelCode = resolvePanelCode(typeName);

  // --- row label occurrence arrays ---
  const codeOcc = bboxMap['code'] ?? [];
  const valueOcc = bboxMap['value'] ?? [];
  const unitOcc = bboxMap['unit'] ?? [];

  // displayName aliases: 'displayName' | 'display' | 'disply'
  // Pick the first non-empty for each index; use whichever alias the doc uses.
  const displayNameOcc = bboxMap['displayName'] ?? [];
  const displayOcc = bboxMap['display'] ?? [];
  const displyOcc = bboxMap['disply'] ?? [];

  const rowCount = Math.max(
    codeOcc.length,
    displayNameOcc.length,
    displayOcc.length,
    displyOcc.length,
    valueOcc.length
  );

  const results: LabResultRow[] = [];

  for (let i = 0; i < rowCount; i++) {
    const rawCode = getText(codeOcc, i);
    const displayName =
      getText(displayNameOcc, i) ||
      getText(displayOcc, i) ||
      getText(displyOcc, i);
    const value = getText(valueOcc, i);
    const unit = getText(unitOcc, i);

    // Skip unit-only rows (nothing useful to show in the form)
    if (!rawCode && !displayName && !value) continue;

    const resolvedCode = resolveObservationCode(
      rawCode,
      displayName,
      panelCode
    );

    results.push({
      id: '',
      category: panelCode,
      code: resolvedCode,
      displayName: displayName || resolvedCode,
      testDate,
      unit,
      value,
    });
  }

  // Fallback: no grouped row labels → single row from flat (single-value OCR path)
  if (results.length === 0) {
    const rawCode = flat.code || '';
    const flatDisplayName = flat.displayName || '';
    const code = resolveObservationCode(rawCode, flatDisplayName, panelCode);
    results.push({
      id: '',
      category: panelCode,
      code,
      displayName: flatDisplayName || code || '',
      testDate,
      unit: flat.unit || '',
      value: flat.value || '',
    });
  }

  return {
    title,
    practitioner,
    facilityName,
    city,
    country,
    code: panelCode,
    vitallCode: panelCode,
    testDate,
    typeName,
    results,
  };
}
